import * as RadixSelect from '@radix-ui/react-select';
import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from './cn';

/**
 * Випадний список на Radix — замість нативного `<select>`.
 *
 * Нативний елемент малює операційна система: у ньому не працюють наші токени,
 * він виглядає інакше в кожному браузері й ламає ряд контролів, бо має власну
 * висоту й шрифт. Radix дає ту саму клавіатуру й доступність, але розмітку
 * малюємо ми — тож він нарешті виглядає як решта інтерфейсу.
 */
export interface SelectOption {
	value: string;
	label: string;
	/** Видно, але не вибрати: наприклад, порт, куди з цього місця не возять. Причина — у `label`. */
	disabled?: boolean;
}

export function Select({
	value,
	onValueChange,
	options,
	placeholder = 'Select…',
	ariaLabel,
	className,
	size = 'md',
	disabled = false,
	id,
	invalid = false,
}: {
	value: string;
	onValueChange: (value: string) => void;
	options: readonly SelectOption[];
	placeholder?: string;
	ariaLabel: string;
	className?: string;
	size?: 'sm' | 'md';
	disabled?: boolean;
	/** Для `<label htmlFor>`: клік по мітці відкриває список, як у звичайного поля. */
	id?: string;
	/** Поле з помилкою валідації: червона межа й `aria-invalid`. */
	invalid?: boolean;
}): ReactNode {
	const selected = options.find((o) => o.value === value);

	return (
		<RadixSelect.Root value={value} onValueChange={onValueChange} disabled={disabled}>
			<RadixSelect.Trigger
				id={id}
				aria-label={ariaLabel}
				aria-invalid={invalid || undefined}
				className={cn(
					'inline-flex items-center justify-between gap-2 rounded-control border border-border-strong bg-surface text-fg transition-colors',
					'hover:border-fg-subtle focus:border-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
					'data-[placeholder]:text-fg-muted disabled:cursor-not-allowed disabled:opacity-60',
					invalid && 'border-danger hover:border-danger focus:border-danger',
					size === 'sm' ? 'h-8 px-2.5 text-xs' : 'h-9 px-3 text-sm',
					className,
				)}
			>
				<RadixSelect.Value placeholder={placeholder}>{selected?.label}</RadixSelect.Value>
				<RadixSelect.Icon>
					<ChevronDown className="size-3.5 text-fg-subtle" />
				</RadixSelect.Icon>
			</RadixSelect.Trigger>

			<RadixSelect.Portal>
				<RadixSelect.Content
					position="popper"
					sideOffset={4}
					className={cn(
						'z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-control border border-border bg-surface shadow-e2',
						'data-[state=open]:animate-in data-[state=open]:fade-in-0',
					)}
				>
					<RadixSelect.Viewport className="p-1">
						{options.map((option) => (
							<RadixSelect.Item
								key={option.value}
								value={option.value}
								disabled={option.disabled}
								className={cn(
									'relative flex cursor-pointer select-none items-center gap-2 rounded-md py-1.5 pl-2 pr-7 text-sm text-fg-muted outline-none',
									'data-[highlighted]:bg-surface-muted data-[highlighted]:text-fg',
									'data-[state=checked]:font-medium data-[state=checked]:text-fg',
									'data-[disabled]:cursor-not-allowed data-[disabled]:text-fg-subtle',
								)}
							>
								<RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
								<RadixSelect.ItemIndicator className="absolute right-2">
									<Check className="size-3.5 text-primary" />
								</RadixSelect.ItemIndicator>
							</RadixSelect.Item>
						))}
					</RadixSelect.Viewport>
				</RadixSelect.Content>
			</RadixSelect.Portal>
		</RadixSelect.Root>
	);
}
